import Phaser from 'phaser';
import type { PublicGame } from '../../api/public-games-types.js';
import { mapsById } from '../../maps/index.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';

// Card renderer for the Inbox LOBBY tab (browse public open games).
// Mirrors InboxGameCard's visual rhythm (same border / pad /
// typography) but tuned for the browser context: no YOUR-TURN
// treatment, no "(you)" roster tag, mode badge instead of status
// badge, "Tap to Join" CTA hint at the bottom.
//
// Kept separate from InboxGameCard because the wire types differ —
// PublicGame has hostUsername, humansSeated, humanCap; InboxGame has
// status, isYourTurn, lastActionAt. Forcing a single component to
// handle both would muddy the prop surface for negligible code-share
// win.

const CARD_RADIUS = 8;
const CARD_PAD_X = 22;
const CARD_PAD_Y = 18;
const CARD_HEIGHT = 156;

const MODE_BADGE_HEX = COLORS.accentCyanHex;

const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}

export interface PublicGameCardOpts {
  readonly scene: Phaser.Scene;
  readonly parent: Phaser.GameObjects.Container;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly game: PublicGame;
  readonly onTap: (game: PublicGame) => void;
}

export interface PublicGameCardResult {
  readonly height: number;
}

export function drawPublicGameCard(
  opts: PublicGameCardOpts
): PublicGameCardResult {
  const { scene, parent, x, y, width, game, onTap } = opts;

  const bg = scene.add.graphics();
  bg.fillStyle(0x0e1a2b, 1);
  bg.fillRoundedRect(x, y, width, CARD_HEIGHT, CARD_RADIUS);
  bg.lineStyle(1, COLORS.borderSubtleInt, 1);
  bg.strokeRoundedRect(x, y, width, CARD_HEIGHT, CARD_RADIUS);
  parent.add(bg);

  // Top row: mode badge (left) + created-age (right).
  const badgeY = y + CARD_PAD_Y;
  drawModeBadge(scene, parent, x + CARD_PAD_X, badgeY, game);
  drawAgeText(scene, parent, x + width - CARD_PAD_X, badgeY, game.createdAt);

  // Map name — large header, dominant element of the row.
  const mapName = (mapsById[game.mapId]?.name ?? game.mapId).toUpperCase();
  const mapY = y + 56;
  const mapText = scene.add
    .text(x + CARD_PAD_X, mapY, mapName, {
      fontFamily: FONT_HEADER,
      fontSize: fs(22),
      color: COLORS.accentCyanHex,
      fontStyle: '700',
    })
    .setLetterSpacing(3)
    .setOrigin(0, 0);
  parent.add(mapText);

  // Mid row: host + seats. "Host: <username> · <seated>/<cap> SEATS"
  const detailY = y + 96;
  const detail = scene.add
    .text(
      x + CARD_PAD_X,
      detailY,
      `Host: ${game.hostUsername}  ·  ${game.humansSeated}/${game.targetSeats} SEATS`,
      {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: COLORS.textSecondary,
      }
    )
    .setLetterSpacing(1)
    .setOrigin(0, 0);
  parent.add(detail);

  // Bottom row: seat swatches + "Tap to join" right-side hint.
  const rosterY = y + CARD_HEIGHT - CARD_PAD_Y - 14;
  drawSeatSwatches(scene, parent, x + CARD_PAD_X, rosterY, game);

  const cta = scene.add
    .text(x + width - CARD_PAD_X, rosterY + 1, 'TAP TO JOIN →', {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: COLORS.accentCyanHex,
      fontStyle: '700',
    })
    .setLetterSpacing(2)
    .setOrigin(1, 0);
  parent.add(cta);

  // Whole card tappable.
  const hit = scene.add
    .zone(x, y, width, CARD_HEIGHT)
    .setOrigin(0, 0)
    .setInteractive({ useHandCursor: true });
  hit.on('pointerup', () => onTap(game));
  parent.add(hit);

  return { height: CARD_HEIGHT };
}

function drawModeBadge(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  x: number,
  y: number,
  g: PublicGame
): void {
  const label = g.mode === 'ffa' ? 'FREE-FOR-ALL' : 'TEAM UP vs AI';
  const padX = 12;
  const padY = 6;

  const text = scene.add
    .text(0, 0, label, {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: '#040713',
      fontStyle: '900',
    })
    .setLetterSpacing(2)
    .setOrigin(0, 0);
  const w = text.width + padX * 2;
  const h = text.height + padY * 2;

  const bg = scene.add.graphics();
  bg.fillStyle(hexToInt(MODE_BADGE_HEX), 1);
  bg.fillRoundedRect(x, y, w, h, h / 2);
  parent.add(bg);
  text.setPosition(x + padX, y + padY);
  parent.add(text);
}

function drawAgeText(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  rightX: number,
  y: number,
  iso: string
): void {
  const t = scene.add
    .text(rightX, y + 3, relativeTime(iso), {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: COLORS.textMuted,
    })
    .setLetterSpacing(1)
    .setOrigin(1, 0);
  parent.add(t);
}

function drawSeatSwatches(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  x: number,
  y: number,
  g: PublicGame
): void {
  let cx = x;
  const swatchSize = 12;
  const swatchGap = 4;
  const sortedSeats = [...g.seats].sort(
    (a, b) => a.seatIndex - b.seatIndex
  );
  for (const seat of sortedSeats) {
    const swatch = scene.add.graphics();
    // Filled for occupied seats, hollow outline for the implicit
    // open slots — but we only know occupied seats here; rendering
    // empty slots accurately would require a per-mode seat-index map.
    // The seats/cap count above conveys openness; the swatches just
    // confirm "this team color is taken."
    swatch.fillStyle(hexToInt(seat.color), 1);
    swatch.fillRoundedRect(cx, y, swatchSize, swatchSize, 2);
    parent.add(swatch);
    cx += swatchSize + swatchGap;
  }
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function hexToInt(hex: string): number {
  let s = hex.startsWith('#') ? hex.slice(1) : hex;
  if (s.length === 3) {
    s = s
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return parseInt(s, 16);
}
